import { useEffect, useState, useCallback } from "react";
import { Loader2, TrendingUp, Radar, MapPin, Boxes, Rocket, Search } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-1 font-mono text-2xl font-bold text-[#0A0F1C]">{value}</p>
    </div>
  );
}

function TopList({ title, icon: Icon, items }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-sm font-semibold text-[#0A0F1C] flex items-center gap-1.5 mb-2"><Icon size={15} /> {title}</p>
      {items && items.length ? items.map((it) => (
        <div key={it.value} className="flex items-center justify-between text-sm py-1 border-b border-slate-50 last:border-0">
          <span className="capitalize text-slate-600 truncate">{it.value || "unspecified"}</span>
          <span className="font-mono text-slate-400">{it.demand}</span>
        </div>
      )) : <p className="text-xs text-slate-400">None yet</p>}
    </div>
  );
}

export default function DemandGaps() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [pipeline, setPipeline] = useState(null);

  const load = useCallback(() => api.get("/admin/demand-gaps").then(({ data }) => setData(data))
    .catch((err) => toast.error(formatApiError(err.response?.data?.detail))).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  const recruit = async (gapId) => {
    setBusy(gapId);
    try {
      await api.post(`/admin/demand-gaps/${gapId}/recruit`);
      toast.success("Recruitment Scout invoked (SIMULATED) — candidates queued for human review");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const discover = async (gapId) => {
    setBusy(gapId + ":discover");
    setPipeline(null);
    try {
      const { data } = await api.post("/admin/leads/scout/discover", { gap_id: gapId });
      setPipeline(data);
      toast.success(`Read-only discovery (${data.provider_mode}): ${data.recommended} recommended, ${data.duplicates_skipped} duplicates skipped`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (loading) return <AppShell title="Demand-Gap Intelligence"><div className="h-40 rounded-xl bg-white border border-slate-200 animate-pulse" /></AppShell>;
  const s = data?.summary || {}; const gaps = data?.gaps || [];

  return (
    <AppShell title="Demand-Gap Intelligence" subtitle="What customers need that the current DEI network cannot yet supply.">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat label="Network gaps" value={s.total_gaps ?? 0} testid="dg-total-gaps" />
        <Stat label="Unmet demand" value={s.total_unmet_demand ?? 0} testid="dg-total-demand" />
        <Stat label="Open insufficient needs" value={s.open_insufficient_needs ?? 0} testid="dg-open-needs" />
        <Stat label="Recruitment opportunities" value={gaps.filter((g) => g.recruitment_opportunity).length} testid="dg-recruit-ops" />
      </div>

      <div className="grid md:grid-cols-3 gap-3 mb-8">
        <TopList title="Top missing counterpart roles" icon={Radar} items={s.top_missing_roles} />
        <TopList title="Top missing categories" icon={Boxes} items={s.top_missing_categories} />
        <TopList title="Top demand geographies" icon={MapPin} items={s.top_geographies} />
      </div>

      <h3 className="font-heading text-lg font-bold text-[#0A0F1C] mb-3 flex items-center gap-2"><TrendingUp size={18} /> Demand gaps</h3>
      {gaps.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-10 text-center text-slate-400" data-testid="dg-empty">
          No demand gaps recorded — the network is currently satisfying customer needs.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-100">
              <th className="p-3">Counterpart role</th><th className="p-3">Category</th><th className="p-3">Geography</th>
              <th className="p-3">Demand</th><th className="p-3">Best internal fit</th><th className="p-3">Status</th><th className="p-3"></th>
            </tr></thead>
            <tbody>
              {gaps.map((g) => (
                <tr key={g.gap_id} data-testid={`dg-row-${g.gap_id}`} className="border-b border-slate-50 last:border-0">
                  <td className="p-3 capitalize font-medium text-[#0A0F1C]">{g.counterpart_role || "any"}</td>
                  <td className="p-3 text-slate-600">{g.category || "—"}</td>
                  <td className="p-3 text-slate-600">{g.geography || "any"}</td>
                  <td className="p-3 font-mono">{g.demand_count}</td>
                  <td className="p-3 font-mono text-slate-500">{g.best_internal_qualified}</td>
                  <td className="p-3"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${g.status === "recruiting" ? "bg-[#DBEAFE] text-[#2563EB]" : "bg-[#FEF3C7] text-[#D97706]"}`}>{g.status}</span></td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1.5">
                      <button onClick={() => discover(g.gap_id)} disabled={busy === g.gap_id + ":discover"} data-testid={`dg-discover-${g.gap_id}`}
                        className="h-8 px-3 rounded-md border border-slate-300 text-[#0A0F1C] text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-60 hover:border-[#2563EB] hover:text-[#2563EB]">
                        {busy === g.gap_id + ":discover" ? <Loader2 className="animate-spin" size={13} /> : <Search size={13} />} Discover (read-only)
                      </button>
                      <button onClick={() => recruit(g.gap_id)} disabled={busy === g.gap_id} data-testid={`dg-recruit-${g.gap_id}`}
                        className="h-8 px-3 rounded-md bg-[#0A0F1C] text-white text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-60 hover:bg-[#1E293B]">
                        {busy === g.gap_id ? <Loader2 className="animate-spin" size={13} /> : <Rocket size={13} />} Launch Scout
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pipeline && (
        <div data-testid="dg-pipeline-result" className="mt-6 rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-[#0A0F1C] flex items-center gap-1.5">
              <Search size={15} /> Read-only discovery trace
              <span className="ml-2 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#D97706]">
                {pipeline.provider_mode} · {pipeline.pipeline}
              </span>
            </p>
            <button onClick={() => setPipeline(null)} className="text-xs text-slate-400 hover:text-slate-600">Dismiss</button>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            {pipeline.candidate_count} discovered · {pipeline.recommended} recommended · {pipeline.duplicates_skipped} duplicates skipped.
            Candidates stop at <b>recommended</b> — a senior human must approve before any (simulated) invitation. No email sent.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead><tr className="text-[11px] uppercase tracking-wide text-slate-400 border-b border-slate-100">
                <th className="p-2">Business</th><th className="p-2">Discovery</th><th className="p-2">Email (Hunter)</th>
                <th className="p-2">KYB / registry</th><th className="p-2">Fit</th><th className="p-2">AI rec</th><th className="p-2">Outcome</th>
              </tr></thead>
              <tbody>
                {(pipeline.trace || []).map((t, i) => (
                  <tr key={i} data-testid={`dg-trace-row-${i}`} className="border-b border-slate-50 last:border-0">
                    <td className="p-2 font-medium text-[#0A0F1C]">{t.legal_name}</td>
                    <td className="p-2 text-slate-500">{t.discovery_source}</td>
                    <td className="p-2 text-slate-500">{t.email_validation?.status}</td>
                    <td className="p-2 text-slate-500">{t.kyb?.legal_existence} {t.kyb?.registry ? `(${t.kyb.registry})` : ""}</td>
                    <td className="p-2 font-mono">{t.fit_score ?? "—"}</td>
                    <td className="p-2 capitalize text-slate-600">{t.ai_recommendation ?? "—"}</td>
                    <td className="p-2">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${t.outcome === "recommended" ? "bg-[#DBEAFE] text-[#2563EB]" : t.outcome === "skipped_duplicate" ? "bg-slate-100 text-slate-500" : "bg-[#FEF3C7] text-[#D97706]"}`}>
                        {t.outcome}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AppShell>
  );
}
